import { useState, useEffect, useCallback } from "react";
import { apiFetch } from '../services/api_service';


const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981", purple: "#8b5cf6",
  cyan: "#06b6d4", red: "#ef4444", pink: "#ec4899", teal: "#14b8a6", orange: "#f97316",
};

const JURISDICTIONS = ["EU","UK","US","AU","SG","JP","HK","BR","CA","CH","NZ","IN","KR","NG"];
const SECTORS = ["oilgas","mining","finance","energy","pharma","tech","transport","realestate","food","apparel","agriculture","manufacturing","chemicals"];
const VCP = ["manufacturer","importer","service_provider","financial_intermediary","retailer"];
const INDUSTRIES = ["oilgas","mining","finance","energy","pharma","tech","transport","realestate","food","apparel","agriculture"];
const VERDICT_COLORS = {material_significant:C.red, material:C.amber, not_material:C.dim, under_assessment:C.muted};
const PRIORITY_COLORS = {critical:C.red, high:C.amber, medium:C.blue, low:C.dim};
const FLAG_COLORS = {R:C.emerald, C:C.amber, V:C.purple};
const STATUS_COLORS = {open:C.muted, evidenced:C.blue, claimed:C.amber, filed:C.emerald, verified:C.teal};

async function api(path, opts) {
  try {
    const r = await fetch(`${API_BASE}/api/v1${path}`, {
      headers: {"Content-Type": "application/json"},
      ...opts,
      body: opts?.body ? JSON.stringify(opts.body) : undefined,
    });
    return r.ok ? await r.json() : null;
  } catch { return null; }
}

const Pill = ({color, children, testId}) => (
  <span data-testid={testId} style={{display:"inline-flex",alignItems:"center",gap:3,padding:"2px 8px",borderRadius:99,fontSize:10,fontWeight:600,background:color+"18",color,border:`1px solid ${color}33`,lineHeight:"16px",whiteSpace:"nowrap"}}>{children}</span>
);

const Stat = ({label, value, color, sub, testId}) => (
  <div data-testid={testId} style={{padding:"12px 14px",borderRadius:8,background:C.card,border:`1px solid ${C.border}`,flex:"1 1 120px",minWidth:100}}>
    <div style={{fontSize:9,color:C.muted,letterSpacing:.5,marginBottom:4,textTransform:"uppercase"}}>{label}</div>
    <div style={{fontSize:22,fontWeight:700,color:color||C.text,lineHeight:1}}>{value}</div>
    {sub && <div style={{fontSize:10,color:C.dim,marginTop:3}}>{sub}</div>}
  </div>
);

function Select({label, value, onChange, options, placeholder, testId}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {label && <label style={{fontSize:10,color:C.muted,letterSpacing:.5}}>{label}</label>}
      <select data-testid={testId} value={value} onChange={e=>onChange(e.target.value)} style={{padding:"7px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:12}}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => typeof o==="string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Input({label, value, onChange, type, placeholder, min, testId}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {label && <label style={{fontSize:10,color:C.muted,letterSpacing:.5}}>{label}</label>}
      <input data-testid={testId} type={type||"text"} value={value} onChange={e=>onChange(type==="number"?Number(e.target.value):e.target.value)} placeholder={placeholder} min={min}
        style={{padding:"7px 10px",borderRadius:6,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:12}} />
    </div>
  );
}

function Btn({children, onClick, color, disabled, small, testId}) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled} style={{padding:small?"4px 10px":"7px 16px",borderRadius:6,border:`1px solid ${color||C.blue}`,background:(color||C.blue)+"18",color:color||C.blue,fontSize:small?10:11,fontWeight:600,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.5:1,transition:"all .15s"}}>{children}</button>
  );
}

function ProgressBar({pct, color, height}) {
  return (
    <div style={{height:height||6,borderRadius:3,background:C.dim+"33",overflow:"hidden"}}>
      <div style={{height:"100%",borderRadius:3,background:color||C.emerald,width:`${Math.min(100,pct)}%`,transition:"width .5s"}} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN 1: ENTITY PROFILE
   ═══════════════════════════════════════════════════════════════ */
function EntityScreen({onNext, setEntity, setFrameworks}) {
  const [form, setForm] = useState({name:"",jurisdiction:"EU",sector:"manufacturing",nace_codes:"C24",employee_count:5000,revenue_eur:2000000000,listed:true,value_chain_position:"manufacturer"});
  const [result, setResult] = useState(null);
  const [fwData, setFwData] = useState(null);
  const [voluntary, setVoluntary] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({...f,[k]:v}));

  const assess = useCallback(async () => {
    setLoading(true);
    const d = await api("/entity/profile", {method:"POST", body:{...form, nace_codes:form.nace_codes.split(",").map(s=>s.trim()), employee_count:Number(form.employee_count), revenue_eur:Number(form.revenue_eur)}});
    setResult(d);
    const fw = await api("/entity/frameworks");
    setFwData(fw);
    setLoading(false);
  }, [form]);

  const addVoluntary = useCallback(async () => {
    const add = Object.entries(voluntary).filter(([,v])=>v).map(([k])=>k);
    if (add.length) await api("/entity/frameworks/voluntary", {method:"POST", body:{add}});
    const my = await api("/entity/my-frameworks");
    if (my) { setFrameworks(my); setEntity(form); onNext(); }
  }, [voluntary, form, onNext, setFrameworks, setEntity]);

  return (
    <div data-testid="entity-screen">
      <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Company profile</div>
      <div style={{fontSize:11,color:C.muted,marginBottom:16}}>Tell us about your company. We auto-detect which frameworks are mandatory.</div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <Input label="Company name" value={form.name} onChange={v=>set("name",v)} placeholder="Stahl AG" testId="input-company-name" />
        <Select label="Jurisdiction" value={form.jurisdiction} onChange={v=>set("jurisdiction",v)} options={JURISDICTIONS} testId="select-jurisdiction" />
        <Select label="Sector" value={form.sector} onChange={v=>set("sector",v)} options={SECTORS} testId="select-sector" />
        <Select label="Value chain position" value={form.value_chain_position} onChange={v=>set("value_chain_position",v)} options={VCP} testId="select-vcp" />
        <Input label="NACE codes (comma-separated)" value={form.nace_codes} onChange={v=>set("nace_codes",v)} placeholder="C24, C25" testId="input-nace-codes" />
        <Input label="Employees" value={form.employee_count} onChange={v=>set("employee_count",v)} type="number" min={0} testId="input-employees" />
        <Input label="Revenue (EUR)" value={form.revenue_eur} onChange={v=>set("revenue_eur",v)} type="number" min={0} testId="input-revenue" />
        <div style={{display:"flex",alignItems:"end",gap:8,paddingBottom:2}}>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.text,cursor:"pointer"}}>
            <input data-testid="checkbox-listed" type="checkbox" checked={form.listed} onChange={e=>set("listed",e.target.checked)} /> Listed on stock exchange
          </label>
        </div>
      </div>

      <Btn testId="btn-detect-frameworks" onClick={assess} disabled={loading || !form.name}>{loading ? "Analyzing..." : "Detect applicable frameworks"}</Btn>

      {result && fwData && (
        <div data-testid="framework-results" style={{marginTop:16}}>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
            <Stat label="Mandatory" value={fwData.mandatory?.length||0} color={C.red} testId="stat-mandatory" />
            <Stat label="Conditional" value={fwData.conditional?.length||0} color={C.amber} testId="stat-conditional" />
            <Stat label="Available" value={fwData.voluntary_available?.length||0} color={C.blue} testId="stat-available" />
            <Stat label="Not applicable" value={fwData.excluded?.length||0} color={C.dim} testId="stat-excluded" />
          </div>

          <div style={{fontSize:11,fontWeight:600,marginBottom:8,color:C.text}}>Framework applicability</div>
          <div data-testid="framework-list" style={{display:"flex",flexDirection:"column",gap:4,marginBottom:14}}>
            {fwData.frameworks?.map(fw => {
              const isMand = fw.applicability==="mandatory";
              const isVol = fw.applicability==="voluntary";
              const isExcl = fw.applicability==="excluded" || fw.applicability==="not_applicable";
              const col = isMand?C.emerald:isVol?C.blue:C.dim;
              return (
                <div key={fw.framework_id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:6,background:C.card,border:`1px solid ${col}22`}}>
                  <Pill color={col}>{(fw.applicability||"").replace("_"," ")}</Pill>
                  <span style={{fontSize:11,fontWeight:600,color:C.text,flex:1}}>{fw.framework_id}</span>
                  <span style={{fontSize:10,color:C.muted,flex:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{fw.reason?.substring(0,60)}</span>
                  {isVol && !isExcl && (
                    <label style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:C.blue,cursor:"pointer"}}>
                      <input type="checkbox" checked={!!voluntary[fw.framework_id]} onChange={e=>setVoluntary(v=>({...v,[fw.framework_id]:e.target.checked}))} /> Adopt
                    </label>
                  )}
                  {fw.csrd_wave && <Pill color={C.cyan}>{fw.csrd_wave}</Pill>}
                </div>
              );
            })}
          </div>

          <Btn testId="btn-continue-frameworks" onClick={addVoluntary} color={C.emerald}>
            Continue with {(fwData.mandatory?.length||0)+(fwData.conditional?.length||0)+Object.values(voluntary).filter(Boolean).length} frameworks
          </Btn>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN 2: MATERIALITY
   ═══════════════════════════════════════════════════════════════ */
function MaterialityScreen({onNext, onBack, entity, setMatData}) {
  const [industry, setIndustry] = useState("");
  const [verdicts, setVerdicts] = useState(null);
  const [loading, setLoading] = useState(false);

  const assess = useCallback(async () => {
    setLoading(true);
    await api("/materiality/assess-industry", {method:"POST", body:{industry}});
    const d = await api("/materiality/verdicts");
    setVerdicts(d);
    setMatData(d);
    setLoading(false);
  }, [industry, setMatData]);

  return (
    <div data-testid="materiality-screen">
      <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>Materiality assessment</div>
      <div style={{fontSize:11,color:C.muted,marginBottom:16}}>Select your industry template to auto-assess which ESRS topics are material.</div>

      <div style={{display:"flex",gap:8,alignItems:"end",marginBottom:16}}>
        <div style={{flex:1}}>
          <Select label="Industry template" value={industry} onChange={setIndustry} options={INDUSTRIES} placeholder="Select industry..." testId="select-industry" />
        </div>
        <Btn testId="btn-run-assessment" onClick={assess} disabled={!industry||loading}>{loading?"Assessing...":"Run assessment"}</Btn>
      </div>

      {verdicts && (
        <div data-testid="materiality-results">
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
            <Stat label="Material" value={verdicts.material} color={C.amber} testId="stat-material" />
            <Stat label="Not material" value={verdicts.not_material} color={C.dim} testId="stat-not-material" />
            <Stat label="Total ESRS" value={verdicts.total} color={C.text} testId="stat-total-esrs" />
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:14}}>
            {verdicts.verdicts?.map(v => {
              const col = VERDICT_COLORS[v.verdict]||C.muted;
              const isMat = v.verdict==="material"||v.verdict==="material_significant";
              return (
                <div key={v.id} data-testid={`verdict-${v.id}`} style={{padding:"8px 10px",borderRadius:6,background:C.card,border:`1px solid ${col}22`,opacity:isMat?1:.5}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                    <Pill color={col}>{v.id}</Pill>
                    <span style={{fontSize:11,fontWeight:600,color:C.text}}>{v.name}</span>
                  </div>
                  <div style={{display:"flex",gap:6,fontSize:10,color:C.muted}}>
                    <span>Verdict: <span style={{color:col,fontWeight:600}}>{v.verdict?.replace("_"," ")}</span></span>
                    <span>Topics: {v.cfi_topic_count}</span>
                    <span>KPIs: {v.kpi_count}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{display:"flex",gap:8}}>
            <Btn testId="btn-back-to-entity" onClick={onBack} color={C.muted}>Back</Btn>
            <Btn testId="btn-generate-requirements" onClick={onNext} color={C.emerald}>Generate requirements</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SCREEN 3: REQUIREMENTS DASHBOARD
   ═══════════════════════════════════════════════════════════════ */
function RequirementsScreen({onBack, entity}) {
  const [report, setReport] = useState(null);
  const [byFw, setByFw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [fwFilter, setFwFilter] = useState("");
  const [reqList, setReqList] = useState(null);

  useEffect(() => {
    (async () => {
      await api("/requirements/generate", {method:"POST"});
      const [r, f] = await Promise.all([
        api("/requirements/compliance-report"),
        api("/requirements/by-framework"),
      ]);
      setReport(r); setByFw(f);
      const list = await api("/requirements?limit=50");
      setReqList(list);
      setLoading(false);
    })();
  }, []);

  const loadFiltered = useCallback(async (fw) => {
    setFwFilter(fw);
    const list = await api(`/requirements?framework=${encodeURIComponent(fw)}&limit=50`);
    setReqList(list);
  }, []);

  const loadAll = useCallback(async () => {
    setFwFilter("");
    const list = await api("/requirements?limit=50");
    setReqList(list);
  }, []);

  if (loading) return <div style={{padding:40,textAlign:"center",color:C.dim}}>Generating disclosure requirements...</div>;

  const tabs = [{id:"overview",label:"Overview"},{id:"frameworks",label:"By framework"},{id:"requirements",label:"All requirements"},{id:"gaps",label:"Gaps"}];

  const byFwData = byFw?.by_framework || {};

  return (
    <div data-testid="requirements-screen">
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div>
          <div style={{fontSize:15,fontWeight:700}}>Disclosure requirements</div>
          <div style={{fontSize:11,color:C.muted}}>{entity?.name || "Company"} — {report?.overall?.total_requirements || 0} obligations across {Object.keys(byFwData).length} frameworks</div>
        </div>
        <Btn testId="btn-back-to-materiality" onClick={onBack} color={C.muted} small>Back</Btn>
      </div>

      <div style={{display:"flex",gap:4,marginBottom:14}}>
        {tabs.map(t => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={()=>setTab(t.id)} style={{padding:"4px 10px",borderRadius:5,border:`1px solid ${tab===t.id?C.blue:C.border}`,background:tab===t.id?C.blue+"18":"transparent",color:tab===t.id?C.blue:C.muted,fontSize:10,fontWeight:600,cursor:"pointer"}}>{t.label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab==="overview" && report && (
        <div data-testid="tab-overview-content">
          <div style={{padding:14,borderRadius:8,background:C.surface,border:`1px solid ${C.border}`,marginBottom:14}}>
            <div data-testid="compliance-statement" style={{fontSize:12,color:C.text,lineHeight:1.6,marginBottom:8}}>{report.overall?.statement}</div>
            <ProgressBar pct={report.overall?.compliance_pct||0} color={report.overall?.compliance_pct>=50?C.emerald:report.overall?.compliance_pct>=20?C.amber:C.red} height={8} />
            <div style={{fontSize:10,color:C.muted,marginTop:4}}>{report.overall?.compliance_pct}% compliant</div>
          </div>

          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
            <Stat label="Total" value={report.overall?.total_requirements} testId="stat-total-reqs" />
            <Stat label="Satisfied" value={report.overall?.satisfied} color={C.emerald} testId="stat-satisfied" />
            <Stat label="Open" value={(report.overall?.total_requirements||0)-(report.overall?.satisfied||0)} color={C.red} testId="stat-open" />
            <Stat label="Compliance" value={`${report.overall?.compliance_pct}%`} color={C.cyan} testId="stat-compliance-pct" />
          </div>

          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14}}>
            {Object.entries(report.by_priority||{}).map(([pri,count]) => <Pill key={pri} color={PRIORITY_COLORS[pri]||C.muted}>{pri}: {count}</Pill>)}
            {Object.entries(report.by_flag||{}).map(([f,count]) => <Pill key={f} color={FLAG_COLORS[f]||C.muted}>{f==="R"?"Required":f==="C"?"Conditional":"Voluntary"}: {count}</Pill>)}
          </div>

          {report.top_gaps?.length > 0 && (
            <div data-testid="top-gaps-section">
              <div style={{fontSize:11,fontWeight:600,color:C.red,marginBottom:6}}>Top gaps</div>
              {report.top_gaps.slice(0,8).map((g,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:4,background:C.card,border:`1px solid ${C.border}`,marginBottom:3}}>
                  <Pill color={PRIORITY_COLORS[g.priority]}>{g.priority}</Pill>
                  <span style={{fontSize:10,color:C.text,flex:1}}>{g.topic}</span>
                  <Pill color={C.blue}>{g.framework}</Pill>
                  <span style={{fontSize:9,color:C.dim}}>{g.action?.substring(0,35)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Frameworks Tab */}
      {tab==="frameworks" && (
        <div data-testid="tab-frameworks-content">
          {Object.entries(byFwData).sort((a,b)=>b[1].total-a[1].total).map(([fw,data]) => {
            const pct = data.completion_pct || data.compliance_pct || 0;
            return (
              <div key={fw} data-testid={`fw-card-${fw.replace(/[^a-zA-Z0-9]/g,'-')}`} style={{padding:10,borderRadius:8,background:C.card,border:`1px solid ${C.border}`,marginBottom:6,cursor:"pointer"}} onClick={()=>{setTab("requirements");loadFiltered(fw);}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:12,fontWeight:600,color:C.text}}>{fw}</span>
                    <Pill color={data.applicability==="mandatory"?C.red:C.blue}>{data.applicability}</Pill>
                    {data.deadline && <span style={{fontSize:9,color:C.amber}}>Due: {data.deadline}</span>}
                  </div>
                  <span style={{fontSize:14,fontWeight:700,color:pct>=50?C.emerald:pct>=20?C.amber:C.muted}}>{pct}%</span>
                </div>
                <ProgressBar pct={pct} color={pct>=50?C.emerald:pct>=20?C.amber:C.red} />
                <div style={{display:"flex",gap:8,marginTop:4,fontSize:9,color:C.muted}}>
                  <span>Total: {data.total}</span>
                  <span>Open: {data.open}</span>
                  <span>Evidenced: {data.evidenced}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Requirements List Tab */}
      {tab==="requirements" && reqList && (
        <div data-testid="tab-requirements-content">
          {fwFilter && (
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
              <span style={{fontSize:10,color:C.muted}}>Filtered: {fwFilter}</span>
              <Btn testId="btn-clear-filter" onClick={loadAll} small color={C.muted}>Clear</Btn>
            </div>
          )}
          <div style={{fontSize:10,color:C.dim,marginBottom:6}}>{reqList.total} requirements</div>
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            {reqList.requirements?.map(r => (
              <div key={r.req_id} data-testid={`req-row-${r.req_id}`} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",borderRadius:4,background:C.card,border:`1px solid ${C.border}`}}>
                <Pill color={FLAG_COLORS[r.flag]}>{r.flag}</Pill>
                <Pill color={STATUS_COLORS[r.status]}>{r.status}</Pill>
                <span style={{fontSize:10,color:C.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.topic}</span>
                <Pill color={C.blue}>{r.framework}</Pill>
                <Pill color={PRIORITY_COLORS[r.priority]}>{r.priority}</Pill>
                {r.filing_deadline && <span style={{fontSize:9,color:C.amber}}>{r.filing_deadline}</span>}
              </div>
            ))}
          </div>
          {reqList.total > (reqList.requirements?.length || 0) && (
            <div style={{textAlign:"center",marginTop:8}}>
              <Btn testId="btn-load-more" onClick={async()=>{
                const nextPage = (reqList.page || 1) + 1;
                const d = await api(`/requirements?page=${nextPage}&limit=50${fwFilter?`&framework=${encodeURIComponent(fwFilter)}`:""}`);
                if(d) setReqList(prev=>({...d, requirements:[...(prev?.requirements||[]),...(d.requirements||[])]}));
              }} small>Load more</Btn>
            </div>
          )}
        </div>
      )}

      {/* Gaps Tab */}
      {tab==="gaps" && report && (
        <div data-testid="tab-gaps-content">
          <div style={{fontSize:11,fontWeight:600,color:C.red,marginBottom:8}}>{report.total_gaps} gaps — requirements with no evidence</div>
          {report.top_gaps?.map((g,i) => (
            <div key={i} data-testid={`gap-row-${i}`} style={{padding:8,borderRadius:6,background:C.card,border:`1px solid ${(PRIORITY_COLORS[g.priority]||C.dim)}22`,marginBottom:4}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                <Pill color={PRIORITY_COLORS[g.priority]}>{g.priority}</Pill>
                <Pill color={FLAG_COLORS[g.flag]}>{g.flag}</Pill>
                <span style={{fontSize:11,fontWeight:600,color:C.text}}>{g.topic}</span>
                <Pill color={C.blue}>{g.framework}</Pill>
              </div>
              <div style={{fontSize:10,color:C.muted}}>{g.action}</div>
              {g.deadline && <div style={{fontSize:9,color:C.amber,marginTop:2}}>Deadline: {g.deadline}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════════════ */
export default function ComplianceCommandCenter() {
  const [step, setStep] = useState(0);
  const [entity, setEntity] = useState(null);
  const [frameworks, setFrameworks] = useState(null);
  const [matData, setMatData] = useState(null);

  const steps = [
    {label:"Entity profile",icon:"1",desc:"Who you are"},
    {label:"Materiality",icon:"2",desc:"What matters"},
    {label:"Requirements",icon:"3",desc:"What you owe"},
  ];

  return (
    <div data-testid="compliance-command-center" style={{background:C.bg,minHeight:"100vh",color:C.text,fontFamily:"'DM Sans', 'Outfit', system-ui, sans-serif"}}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,background:C.bg+"f0",backdropFilter:"blur(12px)",position:"sticky",top:0,zIndex:10}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:28,height:28,borderRadius:6,background:`linear-gradient(135deg, ${C.teal}, ${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#fff"}}>D</div>
          <div>
            <div style={{fontSize:14,fontWeight:700}}>DisclosureOS</div>
            <div style={{fontSize:8,color:C.muted,letterSpacing:1.2,textTransform:"uppercase"}}>Compliance command center</div>
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:4}}>
            {steps.map((s,i) => (
              <div key={i} data-testid={`step-indicator-${i}`} onClick={()=>i<=step&&setStep(i)} style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:5,cursor:i<=step?"pointer":"default",background:i===step?C.blue+"18":"transparent",border:`1px solid ${i===step?C.blue:i<step?C.emerald+"44":C.border}`,opacity:i>step?.4:1}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:i<step?C.emerald+"33":i===step?C.blue+"33":C.dim+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:i<step?C.emerald:i===step?C.blue:C.dim}}>{i<step?"\u2713":s.icon}</div>
                <div>
                  <div style={{fontSize:10,fontWeight:600,color:i===step?C.blue:i<step?C.emerald:C.muted}}>{s.label}</div>
                  <div style={{fontSize:8,color:C.dim}}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{padding:"20px 24px",maxWidth:960,margin:"0 auto"}}>
        {step===0 && <EntityScreen onNext={()=>setStep(1)} setEntity={setEntity} setFrameworks={setFrameworks} />}
        {step===1 && <MaterialityScreen onNext={()=>setStep(2)} onBack={()=>setStep(0)} entity={entity} setMatData={setMatData} />}
        {step===2 && <RequirementsScreen onBack={()=>setStep(1)} entity={entity} />}
      </div>

      {/* Footer */}
      <div style={{textAlign:"center",padding:"20px 0 30px",fontSize:10,color:C.dim}}>
        Enter data once. <span style={{color:C.emerald,fontWeight:600}}>Defend it everywhere.</span>
      </div>
    </div>
  );
}
